import { MonoTypeOperatorFunction, Observable, of, throwError } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import { ApiFragment, CustomErrorType, FragmentId, Identifier, RecordStatus } from '@stylehaven/components';
import { SearchService } from '../search.service';
import { waitForIndex } from './wait-for-index.helper';

/**
 *
 * @param recordId RecordId of record
 * @param service SearchService to make GET call with
 * @param lastModifiedUpdateResponse MED-4944: Best practice to poll based on the LastModifiedDate of the Update response if possible. This way you know the changes are indexed for sure. In very edge cases some other process could have updated the LastModifiedDate already while user is editing.
 * @returns
 */
export const WaitForRecordUpdate: (
    recordId: FragmentId,
    service: SearchService,
    lastModifiedUpdateResponse: Date,
    strict?: boolean
) => Observable<ApiFragment> = (
    recordId: FragmentId,
    service: SearchService,
    lastModifiedUpdateResponse: Date,
    strict: boolean = false
): Observable<ApiFragment> => {
    return service.getRecord(recordId).pipe(
        switchMap((result: ApiFragment) => {
            if (
                strict
                    ? result.getLastModifiedDate() > lastModifiedUpdateResponse
                    : result.getLastModifiedDate() >= lastModifiedUpdateResponse
            ) {
                return of(result);
            }
            return throwError(() => CustomErrorType.IndexTimeout);
        }),
        waitForIndex()
    );
};

export const WaitForRecordStatusUpdate: (
    recordId: FragmentId,
    service: SearchService,
    originalStatus: RecordStatus
) => Observable<ApiFragment> = (
    recordId: FragmentId,
    service: SearchService,
    originalStatus: RecordStatus
): Observable<ApiFragment> => {
    return service.getRecord(recordId).pipe(
        switchMap((result: ApiFragment) => {
            if (result.getRecordStatus() !== originalStatus) {
                return of(result);
            }
            return throwError(() => CustomErrorType.IndexTimeout);
        }),
        waitForIndex()
    );
};
// Wrapper of the above to use in pipe streams. Returns the input observable value.
export function WaitForRecord<T>(currentRecord: ApiFragment, service: SearchService): MonoTypeOperatorFunction<T> {
    return input$ =>
        input$.pipe(
            switchMap(result =>
                WaitForRecordUpdate(currentRecord.getFragmentId(), service, currentRecord.getLastModifiedDate()).pipe(
                    map(() => result)
                )
            )
        );
}

export function WaitForRecordDelete<T>(
    currentRecord: ApiFragment,
    service: SearchService,
    includeLogicallyDeleted: boolean = false
): MonoTypeOperatorFunction<T> {
    return input$ =>
        input$.pipe(
            switchMap(result =>
                service.checkIfFragmentExists(currentRecord.getFragmentId(), includeLogicallyDeleted).pipe(
                    switchMap((exists: boolean) => {
                        if (exists) {
                            return throwError(() => CustomErrorType.IndexTimeout);
                        } else {
                            return of(true);
                        }
                    }),
                    waitForIndex(),
                    map(() => result)
                )
            )
        );
}

export const WaitForRecordsUploadComplete: (
    recordId: Identifier,
    service: SearchService,
    includeDeleted?: boolean,
    retries?: number
) => Observable<ApiFragment> = (
    recordId: Identifier,
    service: SearchService,
    includeDeleted: boolean = false,
    retries: number = 6
): Observable<ApiFragment> => {
    const uploadStatuses: Set<RecordStatus> = new Set([RecordStatus.Uploaded, RecordStatus.Uploading]);

    return service.getRecord(recordId, { includeDeleted }).pipe(
        catchError(error => {
            const hasStatusUpperCase: boolean =
                Object.prototype.hasOwnProperty.call(error, 'Status') && error.Status.toString() === '404';
            const hasStatusLowerCase: boolean =
                Object.prototype.hasOwnProperty.call(error, 'status') && error.status.toString() === '404';
            if (hasStatusUpperCase || hasStatusLowerCase) {
                return of(undefined);
            }
            return throwError(() => error);
        }),
        switchMap((result: ApiFragment | undefined) => {
            if (result && !uploadStatuses.has(result.getRecordStatus())) {
                return of(result);
            } else {
                return throwError(() => CustomErrorType.IndexTimeout);
            }
        }),
        waitForIndex(retries, 5000)
    );
};
